import React, { Fragment, useState } from 'react';
import Grid from "@mui/material/Grid";
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import FormHelperText from "@mui/material/FormHelperText";
import Chip from "@mui/material/Chip";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import './style.scss';


const categories = [
    {
        title: "Entretenimiento",
        slug: "entretenimiento"
    },
    {
        title: "Experiencias",
        slug: "experiencias"
    },
    {
        title: "Hogar",
        slug: "hogar"
    },
    {
        title: "Luna de miel",
        slug: "luna-de-miel"
    },
    {
        title: "Mascotas",
        slug: "mascotas"
    },
    {
        title: "Vida cotidiana",
        slug: "vida-cotidiana"
    }
];

const countries = [
    {
        name: "México",
        code: "+52",
        flag: "🇲🇽"
    },
    {
        name: "España",
        code: "+34",
        flag: "🇪🇸"
    },
    {
        name: "Francia",
        code: "+33",
        flag: "🇫🇷"
    },
    {
        name: "Países Bajos",
        code: "+31",
        flag: "🇳🇱"
    },
    {
        name: "Canadá",
        code: "+1",
        flag: "🇨🇦"
    },
    {
        name: "Japón",
        code: "+81",
        flag: "🇯🇵"
    }
];

const CheckoutSection = ({ item, sendReceipt }) => {
    const [forms, setForms] = useState({
        from: '',
        phone: '',
        countryCode: '+52', // Default to Mexico
        note: '',
        image: null
    });

    const [errors, setErrors] = useState({
        from: '',
        phone: '',
        countryCode: '',
        note: '',
        image: ''
    });

    const [touched, setTouched] = useState({
        from: false,
        phone: false,
        countryCode: false,
        note: false,
        image: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateField = (name, value) => {
        switch (name) {
            case 'from':
                if (!value.trim()) {
                    return 'Este campo es obligatorio';
                }
                if (value.length > 100) {
                    return 'Máximo 100 caracteres';
                }
                return '';

            case 'phone':
                if (!value.trim()) {
                    return 'Este campo es obligatorio';
                }
                const phoneDigits = value.replace(/\s/g, '');
                if (!/^\d{9,10}$/.test(phoneDigits)) {
                    return 'Debe tener entre 9 y 10 dígitos';
                }
                return '';

            case 'countryCode':
                if (!value) {
                    return 'Debe seleccionar un código de país';
                }
                return '';

            case 'image':
                if (!value) {
                    return 'Debe seleccionar una imagen';
                }
                // Check file size (max 25MB - will be compressed if needed)
                if (value.size > 25 * 1024 * 1024) {
                    return 'La imagen es demasiado grande. Máximo 25MB permitido.';
                }
                // Check file type
                if (!value.type.startsWith('image/') && value.type !== 'application/pdf') {
                    return 'Solo se permiten imágenes y archivos PDF.';
                }
                return '';

            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {
            from: validateField('from', forms.from),
            phone: validateField('phone', forms.phone),
            countryCode: validateField('countryCode', forms.countryCode),
            note: validateField('note', forms.note),
            image: validateField('image', forms.image)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const isFormValid = () => {
        return forms.from.trim() !== '' &&
            /^\d{9,10}$/.test(forms.phone.replace(/\s/g, '')) &&
            forms.countryCode !== '' &&
            forms.image !== null;
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        let fieldValue;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else if (type === 'file') {
            fieldValue = files[0] || null;
        } else {
            fieldValue = value;
        }

        setForms(prev => ({ ...prev, [name]: fieldValue }));

        if (touched[name]) {
            const error = validateField(name, fieldValue);
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleBlur = (name) => {
        setTouched(prev => ({ ...prev, [name]: true }));
        const error = validateField(name, forms[name]);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setForms(prev => ({ ...prev, phone: value }));

        if (touched.phone) {
            const error = validateField('phone', value);
            setErrors(prev => ({ ...prev, phone: error }));
        }
    };

    const handleCountryCodeChange = (e) => {
        const value = e.target.value;
        setForms(prev => ({ ...prev, countryCode: value }));

        if (touched.countryCode) {
            const error = validateField('countryCode', value);
            setErrors(prev => ({ ...prev, countryCode: error }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const allTouched = {
            from: true,
            phone: true,
            countryCode: true,
            note: true,
            image: true
        };
        setTouched(allTouched);

        if (validateForm()) {
            setIsSubmitting(true);
            try {
                const data = {
                    ...forms,
                    country_code: forms.countryCode,
                    product_id: item.id
                }
                const result = await sendReceipt(data);

                // Reload the page after successful submission
                window.location.reload();
            } catch (error) {
                console.error('Error submitting form:', error);
                // You might want to show an error message here
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const category = categories.find(category => category.slug === item.category_slug);

    return (
        <Fragment>
            <Grid className="checkoutWrapper section-padding">
                <Grid className="container" container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'space-around' }}>
                    <Grid md={6} xs={12} sx={{ display: 'flex' }}>
                        <Grid className="cartStatus" sx={{ width: '100%', height: '100%' }}>
                            <Grid container spacing={3} sx={{ height: '100%' }}>
                                <Grid item xs={12} sx={{ height: '100%' }}>
                                    <Grid className="cartTotals" sx={{ height: '100%' }}>
                                        <h4 style={{ fontSize: '2rem' }}>{item.title}</h4>
                                        <Table>
                                            <TableBody>
                                                <TableRow className="totalProduct">
                                                    <TableCell sx={{ fontSize: '1.5rem', alignItems: 'right', border: 'none', padding: 0 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="h5">${item.price} MXN</Typography>
                                                            <Chip label={category?.title} color="primary" sx={{ mx: 2 }} />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '1rem' }}>{item.description}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <img src={`/product/${item.image}`} alt={item.title} />
                                                            {item.sold && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRadius: '8px'
                                                                }}>
                                                                    <span style={{
                                                                        color: 'white',
                                                                        fontSize: '1.2rem',
                                                                        fontWeight: 'bold',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        ¡Muchas gracias!
                                                                        <br />Hemos recibido este regalo.
                                                                        <br />🤗
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {!item.sold && (
                        <Grid md={6} xs={12} sx={{ display: 'flex' }}>
                            <div className="check-form-area" style={{ width: '100%', height: '100%' }}>
                                <Grid className="cuponWrap checkoutCard" sx={{ width: '100%', height: '100%' }}>
                                    <Box
                                        className='cuponForm'
                                        component="form"
                                        sx={{
                                            '& .MuiTextField-root': { m: 1 },
                                        }}
                                        noValidate
                                        autoComplete="off"
                                        onSubmit={handleSubmit}
                                    >
                                        <Card sx={{ m: 1, width: '100%', backgroundColor: '#f5f5f5', }}>
                                            <CardContent>
                                                <Typography sx={{ fontSize: 14, mb: 2 }} color="text.secondary" gutterBottom>
                                                    Datos para transferir
                                                </Typography>
                                                <Typography variant="h6">
                                                    CLABE: <span style={{ fontWeight: 'bold' }}>638180000129594093</span>
                                                </Typography>
                                                <Typography variant="h6">
                                                    BANCO: <span style={{ fontWeight: 'bold' }}>NU MEXICO</span>
                                                </Typography>
                                                <Typography variant="h6">
                                                    NOMBRE: <span style={{ fontWeight: 'bold' }}>DANIELA AVILA GARCIA</span>
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                        <TextField
                                            required
                                            fullWidth
                                            label="De parte de"
                                            type="text"
                                            className="formInput radiusNone"
                                            value={forms.from}
                                            name="from"
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('from')}
                                            error={touched.from && !!errors.from}
                                            helperText={touched.from && errors.from}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                        <Box sx={{ 
                                            display: 'flex', 
                                            gap: 2, 
                                            alignItems: 'flex-end',
                                            m: 1,
                                            width: '100%',
                                            '& .MuiFormControl-root': {
                                                margin: 0
                                            },
                                            '& .MuiTextField-root': {
                                                margin: 0
                                            }
                                        }}>
                                            <FormControl sx={{ minWidth: 120, margin: 0 }}>
                                                <InputLabel id="country-code-label">País</InputLabel>
                                                <Select
                                                    labelId="country-code-label"
                                                    value={forms.countryCode}
                                                    label="País"
                                                    onChange={handleCountryCodeChange}
                                                    onBlur={() => handleBlur('countryCode')}
                                                    error={touched.countryCode && !!errors.countryCode}
                                                >
                                                    {countries.map((country) => (
                                                        <MenuItem key={country.code} value={country.code}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <span>{country.flag}</span>
                                                                <span>{country.code}</span>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {touched.countryCode && errors.countryCode && (
                                                    <FormHelperText error>
                                                        {errors.countryCode}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            <TextField
                                                label="Teléfono celular"
                                                type="tel"
                                                className="formInput radiusNone"
                                                value={forms.phone}
                                                name="phone"
                                                onChange={handlePhoneChange}
                                                onBlur={() => handleBlur('phone')}
                                                error={touched.phone && !!errors.phone}
                                                helperText={touched.phone && errors.phone}
                                                inputProps={{ maxLength: 10 }}
                                                sx={{ flex: 1, margin: 0 }}
                                            />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            label="Escribe una nota para Daniela y Mario"
                                            multiline
                                            rows={4}
                                            className="formInput radiusNone"
                                            value={forms.note || ''}
                                            name="note"
                                            onChange={handleChange}
                                            inputProps={{ maxLength: 255 }}
                                        />
                                        <input
                                            accept="image/*, application/pdf"
                                            style={{ display: 'none' }}
                                            id="image-upload"
                                            type="file"
                                            name="image"
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('image')}
                                        />
                                        <label htmlFor="image-upload" style={{ width: '100%' }}>
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                fullWidth
                                                sx={{ mx: 1, textAlign: 'center' }}
                                            >
                                                {forms.image ? forms.image.name : 'Subir comprobante de transferencia (Imagen o PDF)'}
                                            </Button>
                                        </label>
                                        {touched.image && errors.image && (
                                            <FormHelperText error sx={{ mb: 2 }}>
                                                {errors.image}
                                            </FormHelperText>
                                        )}

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            className="cBtn cBtnBlack"
                                            fullWidth
                                            disabled={!isFormValid() || isSubmitting}
                                            sx={{ mt: 4 }}
                                        >
                                            {isSubmitting ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CircularProgress size={20} color="inherit" />
                                                    Enviando...
                                                </Box>
                                            ) : (
                                                'Listo, he pagado'
                                            )}
                                        </Button>
                                    </Box>
                                </Grid>
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Fragment>
    )
};

export default CheckoutSection;